// Block registry — aggregates all BlockDefinitions into a single typed
// `Record<BlockId, BlockDefinition>` map. Per spec/build-plan.md P-017
// row + spec/tech-arch.md §11.4 + §11.6 (renderEmail walks email.blocks
// and looks up BlockDefinition by blockId here).
//
// **P-037.16 D-116:** `member-shoutout` dropped; `welcome-new-members`
// renamed to `member-list`. **P-037.18 D-118:** `sponsors-needed-cta`
// dropped. **P-037.22 D-130:** `other-notable-events` dropped.
// **P-037.24 D-132:** `big3-signature-events` added. **P-037.28 D-136:**
// `commentary` + `text-block` dropped (replaced by 4 atomic Blocks);
// 9 new Blocks added (`headline`, `text`, `button`, `author`,
// `prospect-list`, `images`, `host-committee`, `big-3-hero-image`,
// `time-location-cost`). **P-037.30 sixth post-Phase-Close §B:**
// `spacer` added (META). Net count: 22 Blocks. (Lineage: 15 pre-
// P-037.16; +1 from `sub-section-heading` at P-037.9 D-109 took it to
// 16; back to 15 after member-shoutout drop + welcome-new-members
// rename at D-116; back to 14 after sponsors-needed-cta drop at D-118;
// back to 13 after other-notable-events drop at D-130; back to 14
// after big3-signature-events add at D-132; up to 21 after D-136 net
// +7; up to 22 after P-037.30 sixth post-Phase-Close §B `spacer` add.)
//
// **First BlockId literal-string union + registry map.** Compile-time
// safety surface for downstream consumers (P-018 renderer, P-024 library
// screen iterates the registry, P-025 form generator reads optionsSchema /
// inputsSchema per Block).
//
// `as const satisfies Record<string, BlockDefinition<any, any>>` is
// load-bearing: it preserves each Block's per-instance generic narrowing
// on the values (so `blocks.commentary.optionsSchema` is the actual narrow
// Zod schema, not the widened type) while still validating the shape
// against the Record constraint. `BlockId = keyof typeof blocks` derives
// the union of all 15 ids — same idiom as `StateId = keyof typeof states`
// (D-071) but for Blocks.
//
// Why `BlockDefinition<any, any>` and not `<z.ZodTypeAny, z.ZodTypeAny>`:
// `z.infer<z.ZodTypeAny>` resolves to `any` for primitive payload types but
// `ZodTypeAny`'s class layout doesn't structurally match `ZodObject` /
// `ZodUnion` schemas under `exactOptionalPropertyTypes` (D-006), so the
// satisfies check fails for every concrete Block. Using `any` in the
// generic slots sidesteps the structural-class mismatch while keeping the
// per-Block narrowing intact on `typeof blocks`.
//
// Keys are kebab-case matching each Block's `.id` field (verified at
// runtime in blocks.test.ts; TS doesn't enforce key === value.id natively).
//
// No BLOCK_IDS non-empty-tuple export yet — STATE_IDS exists at the state
// registry because Zod consumers (events-list, chapter-whatsapp options
// schema) need a `[T, ...T[]]` for `z.enum`. Block ids have no analogous
// Zod-side consumer in V1, so the export is deferred per OC §0
// (no over-engineering, build for actual needs).

import type { BlockDefinition } from '../types'
import { header } from '../blocks/header'
import { footer } from '../blocks/footer'
import { sectionHeading } from '../blocks/section-heading'
import { subSectionHeading } from '../blocks/sub-section-heading'
import { issueDate } from '../blocks/issue-date'
import { spacer } from '../blocks/spacer'
import { headline } from '../blocks/headline'
import { textBlock } from '../blocks/text'
import { buttonBlock } from '../blocks/button'
import { author } from '../blocks/author'
import { dayChairs } from '../blocks/day-chairs'
import { memberList } from '../blocks/member-list'
import { prospectList } from '../blocks/prospect-list'
import { hostCommittee } from '../blocks/host-committee'
import { eventCardGroup } from '../blocks/event-card-group'
import { eventsList } from '../blocks/events-list'
import { big3SignatureEvents } from '../blocks/big3-signature-events'
import { big3HeroImage } from '../blocks/big-3-hero-image'
import { timeLocationCost } from '../blocks/time-location-cost'
import { images } from '../blocks/images'
import { chapterWhatsapp } from '../blocks/chapter-whatsapp'
import { chapterSponsors } from '../blocks/chapter-sponsors'

export const blocks = {
  header,
  footer,
  'section-heading': sectionHeading,
  'sub-section-heading': subSectionHeading,
  'issue-date': issueDate,
  // P-037.30 sixth post-Phase-Close §B: standalone META Block emitting
  // a transparent inter-Block spacer at a user-picked height (30/60/90).
  // The META padding override `{top: 0, bottom: 0}` makes the picker
  // label read additively — see src/blocks/spacer.ts header comment.
  spacer,
  // P-037.28 D-136: 4 atomic Blocks replacing commentary + text-block.
  // Export names suffix `Block` for `text` + `button` to avoid colliding
  // with the `text()` + `button()` primitive function names. `headline`
  // + `author` exports stay unsuffixed (no primitive collision). The
  // Block IDs stay canonical kebab-case at the registry layer.
  headline,
  text: textBlock,
  button: buttonBlock,
  author,
  'day-chairs': dayChairs,
  'member-list': memberList,
  'prospect-list': prospectList,
  'host-committee': hostCommittee,
  'event-card-group': eventCardGroup,
  'events-list': eventsList,
  'big3-signature-events': big3SignatureEvents,
  'big-3-hero-image': big3HeroImage,
  'time-location-cost': timeLocationCost,
  images,
  'chapter-whatsapp': chapterWhatsapp,
  'chapter-sponsors': chapterSponsors,
  // The `any` generics intentionally relax the structural-class match
  // between concrete ZodObject / ZodUnion schemas and the BlockDefinition
  // generic constraint under exactOptionalPropertyTypes (D-006). See file
  // header for the full rationale; per-Block narrowing is preserved in
  // `typeof blocks` so consumers still get the strong types.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as const satisfies Record<string, BlockDefinition<any, any>>

export type BlockId = keyof typeof blocks
